import type {
  AgentInProgressData,
  StreamEvent,
  TimelineActivityEntry,
  TimelineContentKind,
  TimelineContentSegment,
  TimelineToolActivity,
} from '@auw/workspace-utils';

/**
 * Merge a later lifecycle event into an existing step_id row.
 *
 * The backend can emit many progress updates for one logical step and then a
 * terminal update with the same step_id. Keeping only presentation text from
 * the later event leaves the row structurally "in progress" forever, so all
 * typed lifecycle fields must advance together.
 */
/**
 * step_progress 종료 이벤트의 구조화 detail 에서 라이프사이클 신호를 뽑는다.
 *
 * 이 detail 은 문자열 narration 이 아니라 dict 라서 `detail` 누적 경로에서는
 * 버려진다. 완료 판정의 유일한 권위 있는 신호이므로 별도 필드로 보존한다.
 */
export function extractStepLifecycle(
  detail: unknown,
): { planStepId?: string; stepStatus?: StreamEvent['stepStatus'] } {
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return {};
  const d = detail as Record<string, unknown>;
  const rawStatus = typeof d.status === 'string' ? d.status : undefined;
  const stepStatus =
    rawStatus === 'started' || rawStatus === 'completed' || rawStatus === 'failed'
      ? rawStatus
      : undefined;
  const planStepId =
    typeof d.step_id === 'string' && d.step_id.trim() ? d.step_id.trim() : undefined;
  if (!stepStatus && !planStepId) return {};
  return { planStepId, stepStatus };
}

function extractTimelineText(detail: unknown): string {
  if (typeof detail === 'string') return detail;
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return '';
  const text = (detail as Record<string, unknown>).text;
  return typeof text === 'string' ? text : '';
}

function legacyContentKind(
  event: AgentInProgressData['event'] | StreamEvent['event'],
  detail: unknown,
): TimelineContentKind {
  // Before `content_kind` existed, explicit thought deltas reached this layer
  // as strings. `step_progress` objectives use a structured `{ text }` detail
  // despite sharing the legacy `thinking` event name.
  if (event === 'thinking' && typeof detail === 'string') return 'reasoning';
  return 'progress';
}

export function timelineSegmentsFromProgressData(
  data: AgentInProgressData,
): TimelineContentSegment[] {
  const text = extractTimelineText(data.detail);
  if (!text) return [];
  return [{
    kind: data.content_kind ?? legacyContentKind(data.event, data.detail),
    text,
  }];
}

export function timelineToolActivityFromProgressData(
  data: AgentInProgressData,
): TimelineToolActivity | null {
  if (data.event !== 'tool_activity') return null;
  if (!data.detail || typeof data.detail !== 'object' || Array.isArray(data.detail)) {
    return null;
  }
  const detail = data.detail as Record<string, unknown>;
  if (detail.schema !== 'tool-activity.v1') return null;
  const status = detail.status;
  if (
    status !== 'running' &&
    status !== 'completed' &&
    status !== 'failed'
  ) {
    return null;
  }
  const id =
    typeof detail.activity_id === 'string' ? detail.activity_id.trim() : '';
  const agentName =
    typeof detail.agent_name === 'string' && detail.agent_name.trim()
      ? detail.agent_name.trim()
      : data.actor?.name?.trim() ?? '';
  if (!id || !agentName) return null;
  const toolName =
    typeof detail.tool_name === 'string' && detail.tool_name.trim()
      ? detail.tool_name.trim()
      : null;
  const args =
    detail.args && typeof detail.args === 'object' && !Array.isArray(detail.args)
      ? detail.args as Record<string, unknown>
      : null;
  return {
    id,
    status,
    agentName,
    toolName,
    args,
    result: detail.result,
    errorMessage:
      typeof detail.error_message === 'string' && detail.error_message.trim()
        ? detail.error_message.trim()
        : null,
  };
}

export function timelineEntriesFromProgressData(
  data: AgentInProgressData,
): TimelineActivityEntry[] {
  const segments = timelineSegmentsFromProgressData(data);
  if (segments.length > 0) return segments.map((segment) => ({ ...segment }));
  const toolActivity = timelineToolActivityFromProgressData(data);
  return toolActivity
    ? [{ kind: 'tool', activityId: toolActivity.id }]
    : [];
}

function toolActivityNamesMatch(
  current: TimelineToolActivity,
  incoming: TimelineToolActivity,
): boolean {
  return (
    current.agentName === incoming.agentName &&
    (!current.toolName ||
      !incoming.toolName ||
      current.toolName === incoming.toolName)
  );
}

export function upsertTimelineToolActivities(
  current: TimelineToolActivity[],
  incoming: TimelineToolActivity | null,
): TimelineToolActivity[] {
  const next = current.map((activity) => ({ ...activity }));
  if (!incoming) return next;

  let index = next.findIndex((activity) => activity.id === incoming.id);
  if (index < 0) {
    const preferredStatuses =
      incoming.status === 'completed' || incoming.status === 'failed'
        ? new Set(['running'])
        : new Set<string>();
    for (let candidate = next.length - 1; candidate >= 0; candidate -= 1) {
      const activity = next[candidate];
      if (
        preferredStatuses.has(activity.status) &&
        toolActivityNamesMatch(activity, incoming)
      ) {
        index = candidate;
        break;
      }
    }
  }

  if (index < 0) {
    next.push({ ...incoming });
  } else {
    const stableId = next[index].id;
    next[index] = {
      ...next[index],
      ...incoming,
      id: stableId,
      toolName: incoming.toolName ?? next[index].toolName,
      args: incoming.args ?? next[index].args,
      result: incoming.result ?? next[index].result,
      errorMessage: incoming.errorMessage ?? next[index].errorMessage,
    };
  }
  return next;
}

function existingTimelineEntries(
  existing: StreamEvent,
): TimelineActivityEntry[] {
  if (existing.timelineEntries?.length) {
    return existing.timelineEntries.map((entry) => ({ ...entry }));
  }
  const entries: TimelineActivityEntry[] = existingTimelineSegments(existing)
    .map((segment) => ({ ...segment }));
  for (const activity of existing.toolActivities ?? []) {
    entries.push({ kind: 'tool', activityId: activity.id });
  }
  return entries;
}

function appendTimelineEntries(
  current: TimelineActivityEntry[],
  incomingSegments: TimelineContentSegment[],
  incomingTool: TimelineToolActivity | null,
): TimelineActivityEntry[] {
  for (const segment of incomingSegments) {
    if (!segment.text) continue;
    const previous = current[current.length - 1];
    if (previous?.kind === segment.kind) {
      previous.text += segment.text;
    } else {
      current.push({ ...segment });
    }
  }
  if (
    incomingTool &&
    !current.some(
      (entry) =>
        entry.kind === 'tool' && entry.activityId === incomingTool.id,
    )
  ) {
    current.push({ kind: 'tool', activityId: incomingTool.id });
  }
  return current;
}

function existingTimelineSegments(
  existing: StreamEvent,
): TimelineContentSegment[] {
  if (existing.segments?.length) {
    return existing.segments.map((segment) => ({ ...segment }));
  }
  const text = extractTimelineText(existing.detail);
  if (!text) return [];
  return [{
    kind:
      existing.contentKind ??
      legacyContentKind(existing.event, existing.detail),
    text,
  }];
}

function appendTimelineSegments(
  current: TimelineContentSegment[],
  incoming: TimelineContentSegment[],
): TimelineContentSegment[] {
  for (const segment of incoming) {
    if (!segment.text) continue;
    const previous = current[current.length - 1];
    if (previous?.kind === segment.kind) {
      previous.text += segment.text;
    } else {
      current.push({ ...segment });
    }
  }
  return current;
}

export function mergeStepStreamEvent(
  existing: StreamEvent,
  data: AgentInProgressData,
  timestamp = Date.now(),
): StreamEvent {
  const previousDetail =
    typeof existing.detail === 'string' ? existing.detail : '';
  const detailDelta = extractTimelineText(data.detail);
  const segments = appendTimelineSegments(
    existingTimelineSegments(existing),
    timelineSegmentsFromProgressData(data),
  );
  const toolActivities = upsertTimelineToolActivities(
    existing.toolActivities ?? [],
    timelineToolActivityFromProgressData(data),
  );
  const incomingToolActivity = timelineToolActivityFromProgressData(data);
  const storedToolActivity = incomingToolActivity
    ? [...toolActivities]
        .reverse()
        .find((activity) =>
          toolActivityNamesMatch(activity, incomingToolActivity),
        ) ?? null
    : null;
  const timelineEntries = appendTimelineEntries(
    existingTimelineEntries(existing),
    timelineSegmentsFromProgressData(data),
    storedToolActivity,
  );
  const lifecycle = extractStepLifecycle(data.detail);
  const incomingTitle =
    typeof data.title === 'string' && data.title.trim() ? data.title : undefined;
  const canReplaceTitle =
    !!incomingTitle &&
    (
      !existing.title ||
      data.title_source === 'agent' ||
      existing.titleSource !== 'agent'
    );
  const nextActivityStatus =
    existing.activityStatus === 'completed' || existing.activityStatus === 'failed'
      ? existing.activityStatus
      : (data.activity_status ?? existing.activityStatus);

  return {
    ...existing,
    planStepId: lifecycle.planStepId ?? existing.planStepId,
    stepStatus: lifecycle.stepStatus ?? existing.stepStatus,
    phase: data.phase ?? existing.phase,
    event: data.event ?? existing.event,
    channel: data.channel ?? existing.channel,
    contentKind: data.content_kind ?? existing.contentKind,
    segments,
    toolActivities,
    timelineEntries,
    usageSeq: data.usage_seq ?? existing.usageSeq,
    isFinal: data.is_final ?? existing.isFinal,
    /* 이미 보이는 행을 숨길 수 있는 것은 **최종(is_final) 종료 업데이트뿐**이다.
     *
     * 두 종류의 "종료" 가 같은 step_id 로 들어온다.
     *   - step_progress ``completed`` (plan/fast/skill): ``is_final=false`` +
     *     ``visible_in_timeline=false``. 여기서 false 는 "이 종료 이벤트로 **새
     *     행을 만들지 말라**" 는 뜻이다. 이걸 숨김으로 해석하면 스텝이 완료되는
     *     순간 타임라인에서 사라지고, plan 체크리스트는 그 행의 제목으로 완료를
     *     판정하므로 체크가 pending 으로 되돌아간다 (실제 발생한 버그).
     *   - doc-agent read-only 종료: ``is_final=true`` +
     *     ``document_mutation_status='not_applied'``. 준비 중 행 자체가 무의미해
     *     졌으므로 실제로 지워야 한다.
     *
     * 숨겨진 행을 나중에 보이게 승격하는 것은 언제나 허용한다. */
    visibleInTimeline:
      existing.visibleInTimeline === true && data.is_final !== true
        ? true
        : (data.visible_in_timeline ?? existing.visibleInTimeline),
    actor: data.actor ?? existing.actor,
    activityId: data.activity_id ?? existing.activityId ?? data.step_id ?? existing.stepId,
    parentActivityId: data.parent_activity_id ?? existing.parentActivityId,
    activityKind: data.activity_kind ?? existing.activityKind,
    activityStatus: nextActivityStatus,
    sourceActor: data.source_actor ?? existing.sourceActor,
    targetActor: data.target_actor ?? existing.targetActor,
    branchId: data.branch_id ?? existing.branchId,
    titleSource: canReplaceTitle
      ? (data.title_source ?? existing.titleSource)
      : existing.titleSource,
    title: canReplaceTitle ? incomingTitle : existing.title,
    summary:
      data.summary ??
      (canReplaceTitle ? incomingTitle : undefined) ??
      existing.summary,
    detail: previousDetail + detailDelta,
    documentMutationStatus:
      data.document_mutation_status ?? existing.documentMutationStatus,
    timestamp,
  };
}
